/**
 * ════════════════════════════════════════════════════════════════════════════
 *  ⚠️  TEMPORARY AUTHENTICATION — REPLACE WITH ENTRA ID SAML SSO  ⚠️
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Everything in this file that touches *how* a user proves who they are is
 * scaffolding so the app is usable against the emulator today. The rest of the
 * application only ever consumes the `{ user, profile, roles, isAdmin }` shape
 * below, so swapping the provider is a change to ONE function: `signIn()`.
 *
 * ── HOW TO SWAP IN SAML (do this when the real Firebase project exists) ─────
 *
 * 1. Firebase console → Authentication → Sign-in method → Add new provider →
 *    SAML. Give it the provider id `saml.aalrr-entra` (the `saml.` prefix is
 *    required by Firebase). Fill in from the Entra ID enterprise application:
 *      - Entity ID / Issuer      (Entra: "Microsoft Entra Identifier")
 *      - SSO URL                 (Entra: "Login URL")
 *      - X.509 certificate       (Entra: "Certificate (Base64)")
 *    and register the Firebase callback URL
 *    `https://<project>.firebaseapp.com/__/auth/handler` in Entra as the Reply URL.
 *
 * 2. Set `VITE_SAML_PROVIDER_ID=saml.aalrr-entra` in `web/.env`. The code below
 *    already reads it and will prefer SAML whenever it is present — no other
 *    change is needed to switch environments.
 *
 * 3. Delete `signInWithDevPassword`, the <SignIn/> dev panel, and the
 *    `devBootstrapProfile` callable in functions/index.js. With SSO the roster
 *    plus Entra group claims become authoritative for roles.
 *
 * 4. Keep `assertFirmDomain()`. Entra should not be issuing tokens for outside
 *    addresses, but this is cheap defence in depth and the security rules
 *    enforce the same check server-side.
 * ════════════════════════════════════════════════════════════════════════════
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  SAMLAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as fbSignOut,
  updateProfile,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { USING_EMULATORS, auth, db, functions, SAME_ORIGIN_AUTH_ENABLED } from '../firebase';
import { markAutoSignInAttempted, markManualSignIn, markSignedOut } from './autoSignIn';

export const FIRM_DOMAIN = 'aalrr.com';

/** Set this in web/.env once the SAML provider exists; empty means "use dev sign-in". */
// Trimmed defensively: a stray leading/trailing space in the GitHub secret has already
// broken a build-time env var once before (see firebase.js) and would make Firebase
// reject the provider id as unrecognized without an obvious error message.
export const SAML_PROVIDER_ID = (import.meta.env.VITE_SAML_PROVIDER_ID || '').trim();
export const SSO_ENABLED = Boolean(SAML_PROVIDER_ID);

const AuthContext = createContext(null);

function assertFirmDomain(email) {
  if (!email || !email.toLowerCase().endsWith(`@${FIRM_DOMAIN}`)) {
    throw new Error(`Sign-in is restricted to @${FIRM_DOMAIN} accounts.`);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * The result of an automatic redirect sign-in, if we came back from one.
   *
   * Without reading it, a failure is invisible: the redirect returns with no session and no
   * explanation, and the card appears as though nothing had been attempted.
   */
  useEffect(() => {
    if (!SSO_ENABLED || !SAME_ORIGIN_AUTH_ENABLED) return;
    getRedirectResult(auth)
      .then((result) => {
        if (result && result.user) assertFirmDomain(result.user.email);
      })
      .catch((err) => {
        if (err && err.code === 'auth/no-auth-event') return;
        setError(err.message || String(err));
      });
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser) => {
      setUser(fbUser);
      if (!fbUser) {
        setProfile(null);
        setClaims(null);
        setLoading(false);
        return;
      }
      try {
        // Creates users/{uid} on first sign-in and mirrors roster roles into custom
        // claims. Under SSO this is replaced by a claims mapping from Entra groups.
        await httpsCallable(functions, 'devBootstrapProfile')();
        const token = await fbUser.getIdTokenResult(true);
        setClaims(token.claims);
      } catch (err) {
        console.error('[auth] profile bootstrap failed', err);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => setProfile(snap.exists() ? { uid: snap.id, ...snap.data() } : null),
      (err) => console.error('[auth] profile subscription failed', err)
    );
  }, [user]);

  const value = useMemo(() => {
    const roles = profile?.roles || claims?.roles || [];
    // Named once, because two of the flags below are derived from it. Referring to the object's
    // own `isAdmin` property from inside the literal would be a ReferenceError, not a reference.
    const isAdmin = roles.includes('admin') || roles.includes('superadmin');
    return {
      user,
      profile,
      roles,
      loading,
      error,
      isAdmin,
      isSuperAdmin: roles.includes('superadmin'),
      // Deliberately NOT implied by isAdmin/isSuperAdmin: client number, matter number,
      // and matter name are exclusively an Intake action now (see functions/index.js's
      // requireIntake). An admin who does not also hold the Intake role gets isIntake
      // === false here, same as the server-side check.
      isIntake: roles.includes('intake'),
      // Read-only workflow visibility across all matters. Separate from Intake on
      // purpose — grants no approval/conflict-check/numbering powers.
      isWorkflowViewer: roles.includes('workflow_viewer'),
      // A dedicated, narrow permission to manually nudge a pending approval from
      // Routing. It is deliberately independent of Workflow Viewer.
      isReminderSender: roles.includes('reminder_sender'),
      // May edit the wording of the two public forms and nothing else. Admin implies it; the
      // boundary itself is enforced on the server (adminActions.FORM_EDITOR_KEYS), because the
      // questions and the delivery settings share one document.
      isFormEditor: isAdmin || roles.includes('form_editor'),
      isApprover: roles.some((r) => r !== 'attorney' && r !== 'staff'),
      displayName: profile?.displayName || user?.displayName || user?.email || '',

      /** THE SWAP SEAM. Everything else in the app calls only this. */
      async signIn(credentials) {
        setError('');
        // Explicit: clears both the "already tried" and "signed out" flags.
        markManualSignIn();
        try {
          if (SSO_ENABLED) {
            const provider = new SAMLAuthProvider(SAML_PROVIDER_ID);
            const result = await signInWithPopup(auth, provider);
            assertFirmDomain(result.user.email);
            return result.user;
          }
          return await signInWithDevPassword(credentials);
        } catch (err) {
          setError(err.message || String(err));
          throw err;
        }
      },

      /**
       * Signing in without being asked.
       *
       * A REDIRECT, because there is no click behind it and a popup without a user gesture is
       * blocked everywhere. Only offered where `authDomain` is this host — anywhere else the
       * round trip completes at Microsoft and the session cannot be handed back.
       */
      async signInSilently() {
        markAutoSignInAttempted();
        const provider = new SAMLAuthProvider(SAML_PROVIDER_ID);
        await signInWithRedirect(auth, provider);
      },

      signOut: () => {
        // Nothing automatic afterwards: somebody signed straight back in cannot leave, which on a
        // shared machine defeats the button they were using.
        markSignedOut();
        return fbSignOut(auth);
      },
    };
  }, [user, profile, claims, loading, error]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * ⚠️ DEV ONLY. Email + password against the Auth emulator, auto-creating the account
 * so the seeded roster users can be impersonated without any console setup. This is
 * deliberately refused outside the emulator so it cannot survive to production by
 * accident.
 */
async function signInWithDevPassword({ email, password, displayName } = {}) {
  if (!USING_EMULATORS) {
    throw new Error(
      'Dev sign-in is disabled outside the emulator. Configure VITE_SAML_PROVIDER_ID.'
    );
  }
  assertFirmDomain(email);
  try {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  } catch (err) {
    if (!['auth/user-not-found', 'auth/invalid-credential'].includes(err.code)) throw err;
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) await updateProfile(user, { displayName });
    return user;
  }
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
